import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as widgetDataService from '../common/services/next-gen/WidgetDataService';

/**
 * Invalidates all exam-related widget queries (data + counts).
 * Call after any exam modification (mark performed, save, delete, etc.)
 * so every widget on the dashboard refreshes.
 */
export function invalidateAllWidgetQueries(queryClient) {
    queryClient.invalidateQueries({ queryKey: ['widgetData'] });
    queryClient.invalidateQueries({ queryKey: ['widgetCount'] });
}

/**
 * Hook that returns a stable callback to invalidate all widget queries.
 */
export function useInvalidateAllWidgets() {
    const queryClient = useQueryClient();
    return useCallback(() => invalidateAllWidgetQueries(queryClient), [queryClient]);
}

/**
 * Hook to fetch count for a specific widget
 * @param {string} widgetKey - The widget key identifier
 * @param {boolean} [enabled=true] - Whether the query is enabled
 */
export function useWidgetCount(widgetKey, enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', widgetKey],
        queryFn: () => widgetDataService.getWidgetCount(widgetKey),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch full data for a specific widget
 * @template T
 * @param {string} widgetKey - The widget key identifier
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useWidgetData(widgetKey, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', widgetKey],
        queryFn: () => widgetDataService.getWidgetData(widgetKey),
        enabled,
        staleTime: 0, // Always refetch when query becomes active to keep data current
        cacheTime: 10 * 60 * 1000 // 10 minutes - keeps cached data for instant display while refetching
    });
}

/**
 * Hook to fetch counts for multiple widgets
 * @param {string[]} widgetKeys - Array of widget key identifiers
 * @param {boolean} [enabled=true] - Whether the query is enabled
 */
export function useWidgetCounts(widgetKeys, enabled = true) {
    return useQuery({
        queryKey: ['widgetCounts', widgetKeys],
        queryFn: () => widgetDataService.getWidgetCounts(widgetKeys),
        enabled: enabled && widgetKeys.length > 0,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch exams without results
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useExamsWithoutResults(enabled = false) {
    return useWidgetData('exams-without-results', enabled);
}

/**
 * Hook to fetch count of exams without results
 * @param {boolean} [enabled=true] - Whether the query is enabled
 */
export function useExamsWithoutResultsCount(enabled = true) {
    return useWidgetCount('exams-without-results', enabled);
}

/**
 * Hook to fetch exams without results filtered by date range
 * @param {Object|null} request - Request params (startDate, endDate, ianaTimezone)
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useExamsWithoutResultsFiltered(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'exams-without-results-filtered', request],
        queryFn: () => widgetDataService.getExamsWithoutResultsFiltered(request),
        enabled: enabled && request !== null,
        staleTime: 0,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch count of unperformed exams
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/unperformedExamsTypes').UnperformedExamsCountResponse>}
 */
export function useUnperformedExamsCount(enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', 'unperformed-exams'],
        queryFn: () => widgetDataService.getUnperformedExamsCount(),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch unperformed exams data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/unperformedExamsTypes').UnperformedExamsDataResponse>}
 */
export function useUnperformedExamsData(page = 1, pageSize = 50, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'unperformed-exams', page, pageSize],
        queryFn: () => widgetDataService.getUnperformedExamsData(page, pageSize),
        enabled,
        staleTime: 0,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to delete an exam
 * @returns {import('@tanstack/react-query').UseMutationResult<{message: string}, Error, {examId: string, reasonForDeletion: string, deleteBothGroupedExams: boolean}>}
 */
export function useDeleteExam() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: ({ examId, reasonForDeletion, deleteBothGroupedExams }) => 
            widgetDataService.deleteExam(examId, reasonForDeletion, deleteBothGroupedExams),
        onSuccess: () => {
            // Invalidate all widget queries since exam deletion can affect multiple widgets
            invalidateAllWidgetQueries(queryClient);
        }
    });
}

/**
 * Hook to fetch count of follow-up tracking records
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').FollowUpTrackingCountResponse>}
 */
export function useFollowUpTrackingCount(enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', 'follow-up-tracking'],
        queryFn: () => widgetDataService.getFollowUpTrackingCount(),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch follow-up tracking data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').FollowUpTrackingDataResponse>}
 */
export function useFollowUpTrackingData(page = 1, pageSize = 50, enabled = false, search = '') {
    return useQuery({
        queryKey: ['widgetData', 'follow-up-tracking', page, pageSize, search],
        queryFn: () => widgetDataService.getFollowUpTrackingData(page, pageSize, search),
        enabled,
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch count of exams without results filtered by date range
 * @param {Object|null} request - Request params (startDate, endDate, ianaTimezone)
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useExamsWithoutResultsFilteredCount(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetCount', 'exams-without-results-filtered', request],
        queryFn: () => widgetDataService.getExamsWithoutResultsFilteredCount(request),
        enabled: enabled && request !== null,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch count of follow-up tracking filtered records
 * @param {Object|null} request - Request params (startDate, endDate, ianaTimezone, selectedFacilities, selectedBirads)
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useFollowUpTrackingFilteredCount(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetCount', 'follow-up-tracking-filtered', request],
        queryFn: () => widgetDataService.getFollowUpTrackingFilteredCount(request),
        enabled: enabled && request !== null,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch follow-up tracking records filtered by date range, facilities, and BIRADS
 * @param {Object|null} request - Request params (startDate, endDate, ianaTimezone, selectedFacilities, selectedBirads)
 * @param {boolean} [enabled=false] - Whether the query is enabled
 */
export function useFollowUpTrackingFiltered(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'follow-up-tracking-filtered', request],
        queryFn: () => widgetDataService.getFollowUpTrackingFiltered(request),
        enabled: enabled && request !== null,
        staleTime: 0,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch count of incomplete exams
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').IncompleteExamsCountResponse>}
 */
export function useIncompleteExamsCount(enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', 'exams-missing-info'],
        queryFn: () => widgetDataService.getIncompleteExamsCount(),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch incomplete exams data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').IncompleteExamsDataResponse>}
 */
export function useIncompleteExamsData(page = 1, pageSize = 50, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'exams-missing-info', page, pageSize],
        queryFn: () => widgetDataService.getIncompleteExamsData(page, pageSize),
        enabled,
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch count of result letters not printed
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').ResultLetterNotPrintedCountResponse>}
 */
export function useResultLetterNotPrintedCount(enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', 'result-letters-not-printed'],
        queryFn: () => widgetDataService.getResultLetterNotPrintedCount(),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch result letters not printed data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').ResultLetterNotPrintedDataResponse>}
 */
export function useResultLetterNotPrintedData(page = 1, pageSize = 50, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'result-letters-not-printed', page, pageSize],
        queryFn: () => widgetDataService.getResultLetterNotPrintedData(page, pageSize),
        enabled,
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch count of missing same-day provider visits
 * @param {boolean} [enabled=true] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').MissingSameDayProviderVisitsCountResponse>}
 */
export function useMissingSameDayProviderVisitsCount(enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', 'missing-same-day-provider-visits'],
        queryFn: () => widgetDataService.getMissingSameDayProviderVisitsCount(),
        enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000 // 10 minutes
    });
}

/**
 * Hook to fetch missing same-day provider visits data with server-side pagination
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<import('../types/widgetTypes').MissingSameDayProviderVisitsDataResponse>}
 */
export function useMissingSameDayProviderVisitsData(page = 1, pageSize = 50, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'missing-same-day-provider-visits', page, pageSize],
        queryFn: () => widgetDataService.getMissingSameDayProviderVisitsData(page, pageSize),
        enabled,
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch tech summary data
 * @param {Object} request - Tech summary request parameters
 * @param {string} request.startDate - Start date (ISO string)
 * @param {string} request.endDate - End date (ISO string)
 * @param {string[]} request.selectedFacilities - Array of facility IDs
 * @param {string[]} request.selectedTechs - Array of technologist user IDs
 * @param {string[]} request.selectedExamTypes - Array of exam type IDs
 * @param {boolean} request.showDetails - Whether to include exam details
 * @param {string} request.ianaTimezone - Client timezone
 * @param {boolean} [enabled=false] - Whether the query is enabled
 * @returns {import('@tanstack/react-query').UseQueryResult<{techName: string, userId: string, totalExams: number, details: Array}[]>}
 */
export function useTechSummaryData(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'tech-summary', request],
        queryFn: () => widgetDataService.getTechSummaryData(request),
        enabled,
        staleTime: 0,
        cacheTime: 10 * 60 * 1000
    });
}

const STREAM_DONE_KEY = ['widgetStream', 'done'];

/**
 * Streams all widget counts via SSE and writes results into the TanStack Query
 * cache. Sets a sentinel cache key when complete (or on failure) so widgets
 * know when to enable their individual fallback queries.
 */
export function useWidgetCountsStream() {
    const queryClient = useQueryClient();

    useEffect(() => {
        let cleanup = () => {};
        let startTimer = null;
        let unmounted = false;

        function signalDone() {
            queryClient.setQueryData(STREAM_DONE_KEY, true);
        }

        function start() {
            if (unmounted) return;
            // Safety valve: signal done after 10s so widgets can fall back to individual queries
            const timeoutId = setTimeout(() => { if (!unmounted) signalDone(); }, 10000);
            cleanup = widgetDataService.streamWidgetCounts(
                (widgetKey, count) => {
                    queryClient.setQueryData(
                        ['widgetCount', widgetKey],
                        widgetKey === 'exams-without-results'
                            ? count
                            : { widgetKey, mode: 'count', count, data: null }
                    );
                },
                (widgetKey, error) => {
                    console.warn(`Widget stream reported an error for "${widgetKey}":`, error);
                },
                () => { clearTimeout(timeoutId); signalDone(); },
                () => {
                    if (unmounted) return;
                    clearTimeout(timeoutId);
                    signalDone();
                }
            );
        }

        // Defer to the next tick so App.js's useEffect (which sets Globals.nextGenToken)
        // has already run before we attempt the stream connection.
        startTimer = setTimeout(start, 0);

        return () => {
            unmounted = true;
            cleanup();
            clearTimeout(startTimer);
        };
    }, [queryClient]);
}

/**
 * Returns true once the widget count stream has completed (or exhausted retries).
 * Widgets use this to gate their individual count queries � if the stream already
 * populated the cache, TanStack Query will skip the network request entirely.
 */
export function useStreamComplete() {
    const { data } = useQuery({
        queryKey: STREAM_DONE_KEY,
        enabled: false,
        staleTime: Infinity,
    });
    return !!data;
}

/**
 * Hook to fetch count of exam perform failures
 * @param {boolean} [enabled=true]
 */
export function useExamPerformFailuresCount(enabled = true) {
    return useQuery({
        queryKey: ['widgetCount', 'exam-perform-failures'],
        queryFn: () => widgetDataService.getExamPerformFailuresCount(),
        enabled,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Hook to fetch exam perform failures data with server-side pagination
 * @param {number} page
 * @param {number} pageSize
 * @param {boolean} [enabled=false]
 */
export function useExamPerformFailuresData(page = 1, pageSize = 50, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'exam-perform-failures', page, pageSize],
        queryFn: () => widgetDataService.getExamPerformFailuresData(page, pageSize),
        enabled,
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch exam perform failures filtered by date range and facilities
 * @param {Object|null} request - includes startDate, endDate, ianaTimezone, selectedFacilities, page, pageSize
 * @param {boolean} [enabled=false]
 */
export function useExamPerformFailuresFiltered(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetData', 'exam-perform-failures-filtered', request],
        queryFn: () => widgetDataService.getExamPerformFailuresFiltered(request),
        enabled: enabled && request !== null,
        staleTime: 0,
        cacheTime: 10 * 60 * 1000,
        keepPreviousData: true
    });
}

/**
 * Hook to fetch count of exam perform failures filtered
 * @param {Object|null} request
 * @param {boolean} [enabled=false]
 */
export function useExamPerformFailuresFilteredCount(request, enabled = false) {
    return useQuery({
        queryKey: ['widgetCount', 'exam-perform-failures-filtered', request],
        queryFn: () => widgetDataService.getExamPerformFailuresFilteredCount(request),
        enabled: enabled && request !== null,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000
    });
}

/**
 * Mutation hook to reconcile an exam perform failure
 */
export function useReconcileExamPerformFailure() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => widgetDataService.reconcileExamPerformFailure(data),
        onSuccess: () => {
            invalidateAllWidgetQueries(queryClient);
        }
    });
}

/**
 * Mutation hook to dismiss an exam perform failure
 */
export function useDismissExamPerformFailure() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => widgetDataService.dismissExamPerformFailure(id),
        onSuccess: () => {
            invalidateAllWidgetQueries(queryClient);
        }
    });
}







